import { type RoundingMinutes } from "../company_attendance_settings/company_attendance_settings"

/**
 * 打刻記録のまるめ実行
 */
export class AttendanceRounder {
  constructor(private readonly roundOptions: RoundOptions) {}

  /**
   * チェックイン時間を指定された丸め間隔に基づいて丸めます。
   *
   * @param {Date} checkedInAt - 元のチェックイン時間。
   * @returns {Date} 丸められたチェックイン時間。丸めが不要な場合は、元のチェックイン時間をそのまま返します。
   *
   * @remarks
   * このメソッドは、`checkInRoundingUpMinutes` オプションを使用して、丸め間隔を決定します。
   * `checkInRoundingUpMinutes` が設定されていない、または0以下の場合、元の `checkedInAt` 時間が返されます。
   * `checkedInAt` の分がすでに `checkInRoundingUpMinutes` の倍数であれば、丸めは行われません。
   * それ以外の場合、分は次の `checkInRoundingUpMinutes` の倍数に切り上げられます。
   *
   * @example
   * // checkInRoundingUpMinutes が 15 で、checkedInAt が 08:07 の場合
   * const roundedTime = this.roundCheckedInAt(new Date('2023-10-10T08:07:00'));
   * // roundedTime は 08:15 になります。
   */
  roundCheckedInAt(checkedInAt: Date): Date {
    const { checkInRoundingUpMinutes } = this.roundOptions

    // 丸めが必要ない場合（丸めの間隔が設定されていないか、0の場合）、元の時間を返す
    if (!checkInRoundingUpMinutes) {
      return checkedInAt
    }

    // 現在の分を取得し、丸めが必要かどうかを確認
    const currentMinutes = checkedInAt.getMinutes()
    const remainder = currentMinutes % checkInRoundingUpMinutes

    // 余りがない場合、丸めは必要ない
    if (remainder === 0) {
      return checkedInAt
    }

    // 丸めた時間を計算
    const roundedMinutes =
      Math.ceil(currentMinutes / checkInRoundingUpMinutes) *
      checkInRoundingUpMinutes

    const roundedDate = new Date(checkedInAt)
    roundedDate.setMinutes(roundedMinutes)

    return roundedDate
  }

  checkedOutAt(checkedOutAt: Date): Date {
    const { checkOutRoundingDownMinutes } = this.roundOptions

    if (checkOutRoundingDownMinutes != null) {
      const checkedOutAtDate = new Date(checkedOutAt)

      // minutes を再計算して、setMinutes で上書き
      const minutes = this.floor(
        checkedOutAtDate.getMinutes(),
        checkOutRoundingDownMinutes
      )
      checkedOutAtDate.setMinutes(minutes)

      checkedOutAt = checkedOutAtDate
    }

    return checkedOutAt
  }

  protected floor(num: number, radix: number) {
    if (radix === 0) return num

    let str = num.toString(radix)
    str = str.substring(0, str.length - 1) + "0"

    return parseInt(str, radix)
  }
}

/** まるめ設定 */
export interface RoundOptions {
  checkInRoundingUpMinutes: RoundingMinutes | undefined
  checkOutRoundingDownMinutes: RoundingMinutes | undefined
}

/**
 * 打刻時間のまるめ処理に関する情報。
 */
interface RoundingInfo {
  /**
   * 打刻時間がまるめ処理されているかどうか。
   */
  isRounded: boolean

  /**
   * まるめ設定の種類を表す。スケジュールからの設定か、通常まるめの設定か。
   * 設定が存在する場合は、その種類が含まれる。
   * 実際にまるめが行われたかどうかに関わらず、設定がある場合はその情報が含まれます。
   */
  roundingType?: "schedule" | "settings"

  /**
   * まるめ処理に使用された分数（例: 5分刻みの端数処理であれば5）。
   * まるめ設定がない場合、`undefined` が設定されます。
   */
  roundingMinutes?: RoundingMinutes

  /**
   * まるめ処理後の出勤時間。
   * 今後の拡張性を考慮して追加されたフィールドです。現在は返却されません。
   *
   * @see https://github.com/DRIGLO-Inc/kanril_server/issues/232#issuecomment-2650067682
   */
  roundedTime: Date
}

/**
 * 出勤打刻の結果を表すレスポンスのインターフェース。
 */
export interface CheckInResponse {
  rounding: RoundingInfo
}

/**
 * 退勤打刻の結果を表すレスポンスのインターフェース。
 */
export interface CheckOutResponse {
  /**
   * 休日出勤の申請ID。
   * 残業許可制がONかつ、基本設定で休日と設定された曜日に打刻した場合に設定されます。
   * 条件に合致しない場合、このフィールドは設定されません。
   * 詳細については、以下のリンクを参照してください。
   *
   * @see {@link https://www.notion.so/driglo/a5395729309449ac848fa06f022944fe?pvs=4#5292ac874b6f4e43ac48a178509ce8f2}
   */
  holidayWorkApplicationId?: string

  /**
   * 退勤打刻に関する情報。
   * 出勤打刻と退勤打刻のまるめ処理の詳細が含まれます。
   */
  rounding: {
    /**
     * 出勤打刻に関するまるめ処理情報。
     */
    checkIn: RoundingInfo

    /**
     * 退勤打刻に関するまるめ処理情報。
     */
    checkOut: RoundingInfo
  }
}
